/* import __COLOCATED_TEMPLATE__ from './ai-agent-event.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import type RenderablePart from 'embercom/objects/inbox/renderable-part';
import type AiAgentEvent from 'embercom/objects/inbox/renderable/ai-agent-event';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import type RouterService from '@ember/routing/router-service';
import { type AiAgentEventData } from 'embercom/objects/inbox/ai-agent-event-data';
import { AiAgentEventType } from 'embercom/objects/inbox/renderable/ai-agent-event';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { truncateEventText, isEventTextTruncated } from 'embercom/lib/inbox2/event-text-truncation';

interface AiAgentEventPart extends RenderablePart {
  renderableData: AiAgentEvent;
}

interface Signature {
  Args: {
    part: AiAgentEventPart;
  };
}

export default class Inbox2ConversationStreamEventPartsAiAgentEventComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare session: Session;
  @service declare router: RouterService;
  @service declare intercomEventService: any;

  @tracked openReasoningSection = '';

  get EVENT_TYPES() {
    // expose the AiAgentEventType enum for use in the template
    return AiAgentEventType;
  }

  get eventType() {
    return this.args.part.renderableData.eventType;
  }

  get eventDataFromBackend(): AiAgentEventData[keyof AiAgentEventData] | {} {
    // Get the event data passed from the backend for this event
    // You can override any of these by setting them in the additionalEventData method below
    let eventData = this.args.part.renderableData.eventData;

    if (eventData && this.eventType in eventData) {
      return eventData[this.eventType as keyof AiAgentEventData] || {};
    } else {
      return {};
    }
  }

  get isTaskEvent() {
    return (
      this.eventType === AiAgentEventType.AiAgentWorkflowStarted ||
      this.eventType === AiAgentEventType.AiAgentWorkflowFinished ||
      this.eventType === AiAgentEventType.AiAgentWorkflowFailed ||
      this.eventType === AiAgentEventType.AiAgentWorkflowStopped
    );
  }

  get additionalEventData(): { icon?: InterfaceIconName } {
    // Add any additional parameters to pass as a hash to the i18n helper
    switch (this.eventType) {
      case AiAgentEventType.OtpEmailVerificationSuccessful:
        return { icon: 'locked' };
      case AiAgentEventType.OtpEmailVerificationFailed:
      case AiAgentEventType.OtpEmailVerificationUnsuccessful:
        return { icon: 'locked' };
      case AiAgentEventType.OtpEmailAlreadyVerified:
        return { icon: 'locked' };
      case AiAgentEventType.OtpEmailTriggeredForVerification:
        return { icon: 'webhook' };
      case AiAgentEventType.AiAgentWorkflowStarted:
      case AiAgentEventType.AiAgentWorkflowFinished:
      case AiAgentEventType.AiAgentWorkflowFailed:
      case AiAgentEventType.AiAgentWorkflowStopped:
        return { icon: 'custom-attribute' };
      case AiAgentEventType.SdrRoutingDecision:
        return { icon: 'transfer' };
      case AiAgentEventType.AiAgentSetAttributes:
        return { icon: 'transfer' };
      default:
        return {};
    }
  }

  get eventData() {
    return { ...this.eventDataFromBackend, ...this.additionalEventData };
  }

  get eventIcon(): InterfaceIconName {
    // We only use this in contexts that already guard on the presence of an icon
    // (e.g. `{{else if this.eventData.icon}}` in the template), so assert non-null
    return (this.eventData as { icon?: InterfaceIconName }).icon as InterfaceIconName;
  }

  get translatedEvent() {
    if (this.eventType === AiAgentEventType.AiAgentIgnoringUserMessage) {
      return this.intl.t(
        `inbox.ai-agent-events.${this.eventType}.${(this.eventData as AiAgentEventData[AiAgentEventType.AiAgentIgnoringUserMessage])?.reason || 'unknown'}`,
      );
    }

    if (Object.keys(this.eventData).includes('icon')) {
      // If there's an {icon} in your translated string, we need to use ic-interface-icon helper in the template directly as we can't do it here
      // You'll need to add an if case in the template and pass all the other params manually as a hash in the template to the {{t}} helper
      return null;
    } else if (Object.keys(this.eventData).length > 0) {
      return this.intl.t(`inbox.ai-agent-events.${this.eventType}`, this.eventData);
    } else {
      return this.intl.t(`inbox.ai-agent-events.${this.eventType}`);
    }
  }

  get reasoningText(): string {
    if (this.eventType === AiAgentEventType.AiCategoriesReasoning) {
      return (this.eventData as { reasoning: string })?.reasoning || '';
    }
    if (this.eventType === AiAgentEventType.AiAgentWorkflowStarted) {
      return (this.eventData as { reasoning?: string })?.reasoning || '';
    }
    if (this.eventType === AiAgentEventType.AiAgentWorkflowFailed) {
      let reasoning = (this.eventData as { reasoning?: string })?.reasoning;
      if (reasoning === 'system_error') {
        return this.intl.t('inbox.ai-agent-events.ai_agent_workflow_failed_system_error');
      }
      return reasoning || '';
    }
    return '';
  }

  get routingReason(): string {
    if (this.eventType === AiAgentEventType.SdrRoutingDecision) {
      return (this.eventData as { routingOutcomeReason: string })?.routingOutcomeReason || '';
    }
    return '';
  }

  get attributes() {
    if (this.eventType === AiAgentEventType.AiAgentSetAttributes) {
      return (
        (this.eventData as AiAgentEventData[AiAgentEventType.AiAgentSetAttributes])?.attributes ||
        []
      );
    }
    return [];
  }

  get firstAttribute() {
    return this.attributes[0];
  }

  get remainingAttributesCount() {
    return Math.max(0, this.attributes.length - 1);
  }

  @action
  toggleReasoningSection(sectionId: string) {
    let isOpening = this.openReasoningSection !== sectionId;
    this.openReasoningSection = isOpening ? sectionId : '';
    if (isOpening) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'expanded',
        object: 'fin_thoughts',
        place: 'conversation_stream',
        event_type: this.eventType,
      });
    }
  }

  truncateTitle(title: string | undefined): string {
    return truncateEventText(title);
  }

  isTitleTruncated(title: string | undefined): boolean {
    return isEventTextTruncated(title);
  }

  // Type-safe getters for template usage
  get eventDataTitle(): string | undefined {
    return (this.eventData as { title?: string }).title;
  }

  get eventDataRuleName(): string | undefined {
    return (this.eventData as { ruleName?: string }).ruleName;
  }

  get eventDataRulesetId(): string | undefined {
    return (this.eventData as { rulesetId?: string }).rulesetId;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationStream::EventParts::AiAgentEvent': typeof Inbox2ConversationStreamEventPartsAiAgentEventComponent;
    'inbox2/conversation-stream/event-parts/ai-agent-event': typeof Inbox2ConversationStreamEventPartsAiAgentEventComponent;
  }
}
